import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ArrowLeft, ArrowRight, Check, CheckCircle2 } from 'lucide-react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import ApWordmark from '../components/platform/ApWordmark';
import FlashcardDemo from '../components/onboarding/FlashcardDemo';
import TutorDemo from '../components/onboarding/TutorDemo';
import { useApCourses } from '../contexts/ApCourseContext';
import { usePublicSession } from '../contexts/PublicSessionContext';
import { AP_COURSE_FAMILIES, getApCourse, MAX_AP_COURSES_PER_STUDENT } from '../lib/apCourses';
import { supabase, isSupabaseConfigured } from '../lib/supabase';
import { isLocalPreviewMode } from '../lib/localPreview';
import { hasActivePaidAccess } from '../utils/subscriptionAccess';
import '../components/platform/platform.css';
import './onboarding.css';

const STEPS = ['hook', 'name', 'subjects', 'target', 'voice', 'turn', 'tutor', 'flashcards', 'sign'];
const MAX_SUBJECTS = MAX_AP_COURSES_PER_STUDENT;
const AUTO_ADVANCE_MS = 260;
const SIGNATURE_REVEAL_MS = 520;
const SIGNED_HANDOFF_MS = 680;

const TARGETS = [
  { id: '5', title: '5', detail: 'The top score' },
  { id: '4', title: '4', detail: 'Strong and safe' },
  { id: '3', title: '3', detail: 'Pass it' },
];

// Private thoughts AP students post about in every subject, in their own wording (r/APStudents research, Sept–Oct 2026).
const VOICE = [
  { id: 'exam_differs', thought: 'I do the homework and understand it, then the test goes badly.' },
  { id: 'unexplained_points', thought: 'I studied for hours and I still can’t tell where the points went.' },
  { id: 'others_find_easier', thought: 'Everyone else seems to find this easier than I do.' },
  { id: 'careless_mistakes', thought: 'I keep making stupid mistakes.' },
  { id: 'first_bad_grades', thought: 'I’ve never had grades like this before.' },
];

function wait(milliseconds) {
  return new Promise((resolve) => window.setTimeout(resolve, milliseconds));
}

function listCourses(courses) {
  const names = courses.map((course) => course.shortTitle);
  if (names.length <= 2) return names.join(' and ');
  return `${names.slice(0, -1).join(', ')} and ${names.at(-1)}`;
}

async function playSignatureSound() {
  const AudioContextClass = window.AudioContext || window.webkitAudioContext;
  if (!AudioContextClass) return;
  try {
    const context = new AudioContextClass();
    if (context.state !== 'running') await context.resume();
    const duration = 0.42;
    const frameCount = Math.floor(context.sampleRate * duration);
    const buffer = context.createBuffer(1, frameCount, context.sampleRate);
    const data = buffer.getChannelData(0);
    for (let frame = 0; frame < frameCount; frame += 1) {
      const progress = frame / frameCount;
      const penPressure = 0.48 + 0.52 * Math.abs(Math.sin(progress * Math.PI * 5.5));
      data[frame] = (Math.random() * 2 - 1) * Math.sin(Math.PI * progress) * penPressure * 0.48;
    }
    const stroke = context.createBufferSource();
    const filter = context.createBiquadFilter();
    const gain = context.createGain();
    stroke.buffer = buffer;
    filter.type = 'bandpass';
    filter.frequency.value = 1850;
    filter.Q.value = 0.58;
    gain.gain.setValueAtTime(0.0001, context.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.2, context.currentTime + 0.018);
    gain.gain.exponentialRampToValueAtTime(0.0001, context.currentTime + duration);
    stroke.connect(filter);
    filter.connect(gain);
    gain.connect(context.destination);
    stroke.addEventListener('ended', () => void context.close(), { once: true });
    stroke.start();
  } catch {
    // Signing sound is an enhancement and must never block onboarding.
  }
}

function Option({ selected, onClick, children, multi = false }) {
  return (
    <button className={`onb-option ${selected ? 'is-selected' : ''}`} aria-pressed={selected} type="button" onClick={onClick}>
      <span>{children}</span>
      <i className={multi ? 'is-check' : 'is-radio'} aria-hidden="true">{selected ? <Check /> : null}</i>
    </button>
  );
}

// Staggers a screen's parts in once, after the screen transition settles.
function Reveal({ index = 0, children, className = '' }) {
  const reduceMotion = useReducedMotion();
  return (
    <motion.div
      className={`onb-reveal ${className}`.trim()}
      initial={reduceMotion ? false : { opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.12 + index * 0.09, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}

export default function OnboardingPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { selectedSlugs: savedSlugs, saveCourses } = useApCourses();
  const { user, profile, loading, signOut } = usePublicSession();
  const isManaging = new URLSearchParams(location.search).get('mode') === 'subjects';
  const savedName = profile?.full_name || user?.user_metadata?.full_name || '';
  const [stepIndex, setStepIndex] = useState(isManaging ? STEPS.indexOf('subjects') : 0);
  const [direction, setDirection] = useState(1);
  const [name, setName] = useState(savedName);
  const [selectedSlugs, setSelectedSlugs] = useState(isManaging ? savedSlugs : []);
  const [target, setTarget] = useState('');
  const [voice, setVoice] = useState([]);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [signatureState, setSignatureState] = useState('idle');
  const hasPrefilledName = useRef(Boolean(savedName));
  const signingLock = useRef(false);

  const selectedCourses = useMemo(() => selectedSlugs.map(getApCourse).filter(Boolean), [selectedSlugs]);
  const step = STEPS[stepIndex];
  const firstName = name.trim().split(/\s+/)[0] || '';
  const now = useMemo(() => new Date(), []);
  const signatureDate = useMemo(() => new Intl.DateTimeFormat('en-US', { day: 'numeric', month: 'long', year: 'numeric' }).format(now), [now]);
  const signatureVisible = signatureState === 'signing' || signatureState === 'signed';

  useEffect(() => {
    if (hasPrefilledName.current || name || !savedName) return;
    setName(savedName);
    hasPrefilledName.current = true;
  }, [name, savedName]);

  function goTo(index) {
    setError('');
    setDirection(index > stepIndex ? 1 : -1);
    setStepIndex(Math.max(0, Math.min(index, STEPS.length - 1)));
  }

  function next() {
    const message = validate();
    if (message) {
      setError(message);
      return;
    }
    if (isManaging && step === 'subjects') {
      void finish();
      return;
    }
    goTo(stepIndex + 1);
  }

  function back() {
    goTo(stepIndex - 1);
  }

  function chooseAndAdvance(setter, value) {
    setter(value);
    setError('');
    window.setTimeout(() => goTo(stepIndex + 1), AUTO_ADVANCE_MS);
  }

  function toggleVoice(id) {
    setError('');
    setVoice((current) => (current.includes(id) ? current.filter((entry) => entry !== id) : [...current, id]));
  }

  function toggleSubject(slug) {
    setError('');
    setSelectedSlugs((current) => {
      if (current.includes(slug)) return current.filter((entry) => entry !== slug);
      if (current.length >= MAX_SUBJECTS) {
        setError(`Up to ${MAX_SUBJECTS} courses.`);
        return current;
      }
      return [...current, slug];
    });
  }

  function validate() {
    if (step === 'name' && !name.trim()) return 'Add your name to continue.';
    if (step === 'subjects' && selectedSlugs.length === 0) return 'Pick at least one course.';
    return '';
  }

  async function saveOnboarding() {
    const activeSlug = await saveCourses(
      selectedSlugs,
      savedSlugs.find((slug) => selectedSlugs.includes(slug)) || selectedSlugs[0],
    );
    const onboardingContext = {
      display_name: name.trim(),
      selected_courses: selectedSlugs,
      active_course: activeSlug,
      target_score: target,
      inner_voice: voice,
      source: 'ap_multi_subject_onboarding_v5',
    };
    if (!isManaging && user?.id && isSupabaseConfigured && !isLocalPreviewMode()) {
      const { error: profileError } = await supabase.from('profiles').update({
        full_name: name.trim(),
        onboarding_completed: true,
        onboarding_completed_at: new Date().toISOString(),
        onboarding_context: onboardingContext,
      }).eq('id', user.id);
      if (profileError) throw profileError;
    }
    return onboardingContext;
  }

  function openSavedOnboarding(onboardingContext) {
    if (isManaging || hasActivePaidAccess(profile)) {
      navigate('/home', { replace: true });
    } else {
      navigate('/membership', { replace: true, state: { onboardingContext } });
    }
  }

  async function finish() {
    setSaving(true);
    setError('');
    try {
      openSavedOnboarding(await saveOnboarding());
    } catch (cause) {
      setError(cause?.message || 'We could not save your courses. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  async function signAndFinish() {
    if (signingLock.current) return;
    signingLock.current = true;
    setSaving(true);
    setError('');
    setSignatureState('signing');
    const startedAt = Date.now();
    void playSignatureSound();
    try {
      const onboardingContext = await saveOnboarding();
      const remaining = Math.max(0, SIGNATURE_REVEAL_MS - (Date.now() - startedAt));
      if (remaining) await wait(remaining);
      setSignatureState('signed');
      await wait(SIGNED_HANDOFF_MS);
      openSavedOnboarding(onboardingContext);
    } catch (cause) {
      signingLock.current = false;
      setSignatureState('error');
      setError(cause?.message || 'We could not save your plan. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  async function handleExit() {
    if (isManaging) {
      navigate('/home');
      return;
    }
    await signOut?.();
    navigate('/', { replace: true });
  }

  if (loading) {
    return <main className="onb-page"><div className="onb-loading" role="status">Loading…</div></main>;
  }
  if (!user) {
    return <Navigate to="/signup?redirect=%2Fonboarding" replace />;
  }

  const showFooter = !['hook', 'target', 'sign'].includes(step);
  const progress = (stepIndex + 1) / STEPS.length;

  return (
    <main className={`onb-page is-${step}`}>
      <header className="onb-header">
        {stepIndex > 0 && !isManaging ? (
          <button className="onb-icon-button" type="button" aria-label="Back" disabled={saving} onClick={back}><ArrowLeft /></button>
        ) : <ApWordmark compact />}
        <div className="onb-progress" role="progressbar" aria-label={`Step ${stepIndex + 1} of ${STEPS.length}`} aria-valuenow={stepIndex + 1} aria-valuemin={1} aria-valuemax={STEPS.length}>
          <motion.span animate={{ width: `${progress * 100}%` }} transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }} />
        </div>
        <button className="onb-text-button" type="button" disabled={saving} onClick={handleExit}>{isManaging ? 'Close' : 'Sign out'}</button>
      </header>

      <AnimatePresence mode="wait" custom={direction} initial={false}>
        <motion.section
          key={step}
          className={`onb-screen is-${step}`}
          initial={{ opacity: 0, x: 28 * direction }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -28 * direction }}
          transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
        >
          {step === 'hook' ? (
            <>
              <Reveal index={0}><h1>I’ve always been good at school, so why is this the class I can’t crack?</h1></Reveal>
              <Reveal index={3}><button className="onb-primary" type="button" onClick={next}>Get started <ArrowRight /></button></Reveal>
            </>
          ) : null}

          {step === 'name' ? (
            <>
              <h1>First, what’s your name?</h1>
              <input
                className="onb-name-input"
                aria-label="Your name"
                value={name}
                onChange={(event) => { hasPrefilledName.current = true; setName(event.target.value); }}
                onKeyDown={(event) => { if (event.key === 'Enter') next(); }}
                placeholder="Your name"
                autoFocus
              />
            </>
          ) : null}

          {step === 'subjects' ? (
            <>
              <h1>{firstName && !isManaging ? `${firstName}, which APs are you taking?` : 'Which APs are you taking?'}</h1>
              <p className="onb-sub">{selectedSlugs.length} selected · up to {MAX_SUBJECTS}</p>
              <div className="onb-course-groups">
                {Object.entries(AP_COURSE_FAMILIES).map(([family, courses]) => (
                  <section key={family}>
                    <h2>{family}</h2>
                    <div>
                      {courses.map((course) => {
                        const selected = selectedSlugs.includes(course.slug);
                        return (
                          <button className={`onb-chip ${selected ? 'is-selected' : ''}`} aria-pressed={selected} type="button" key={course.slug} onClick={() => toggleSubject(course.slug)}>
                            {selected ? <Check aria-hidden="true" /> : null}{course.shortTitle}
                          </button>
                        );
                      })}
                    </div>
                  </section>
                ))}
              </div>
            </>
          ) : null}

          {step === 'target' ? (
            <>
              <h1>What score are you going for?</h1>
              <div className="onb-score-grid">
                {TARGETS.map((option) => (
                  <button className={`onb-score ${target === option.id ? 'is-selected' : ''}`} aria-pressed={target === option.id} aria-label={`Score ${option.title}`} type="button" key={option.id} onClick={() => chooseAndAdvance(setTarget, option.id)}>
                    <strong>{option.title}</strong><small>{option.detail}</small>
                  </button>
                ))}
              </div>
            </>
          ) : null}

          {step === 'voice' ? (
            <>
              <h1>Tick the ones you’ve thought.</h1>
              <div className="onb-options">
                {VOICE.map((entry, index) => (
                  <Reveal key={entry.id} index={index}>
                    <Option multi selected={voice.includes(entry.id)} onClick={() => toggleVoice(entry.id)}>{entry.thought}</Option>
                  </Reveal>
                ))}
              </div>
            </>
          ) : null}

          {step === 'turn' ? (
            <>
              <Reveal index={0}><p className="onb-turn-pressure">In May nobody grades how hard I worked.</p></Reveal>
              <Reveal index={9}><h1>Every point I lost has a reason, and the reason is written down.</h1></Reveal>
              <Reveal index={14}><p className="onb-sub">Built on College Board’s own questions, rubrics, scored answers and Chief Reader reports.</p></Reveal>
            </>
          ) : null}

          {step === 'tutor' ? (
            <>
              <Reveal index={0}><p className="onb-eyebrow">AI Tutor</p></Reveal>
              <Reveal index={1}><h1>The question I’m stuck on tonight, answered the way the graders score it.</h1></Reveal>
              <Reveal index={2} className="onb-demo"><TutorDemo /></Reveal>
            </>
          ) : null}

          {step === 'flashcards' ? (
            <>
              <Reveal index={0}><p className="onb-eyebrow">Flashcards</p></Reveal>
              <Reveal index={1}><h1>Any point on my syllabus, made into a deck for me.</h1></Reveal>
              <Reveal index={2} className="onb-demo"><FlashcardDemo /></Reveal>
            </>
          ) : null}

          {step === 'sign' ? (
            <>
              <article className="onb-pledge">
                <img className="onb-pledge-ornament" src="/images/onboarding/ap-pledge-ornament.png" alt="" aria-hidden="true" />
                <p>{selectedCourses.length > 1 ? `I’m going to get a ${target || '5'} on every AP exam I take this May.` : `I’m going to get a ${target || '5'} in ${listCourses(selectedCourses)}.`}</p>
                <div className="onb-pledge-signature">
                  {signatureVisible ? <strong>{name.trim()}</strong> : <span>Your signature</span>}
                </div>
                <div className="onb-pledge-date"><span>Date</span><time>{signatureDate}</time></div>
                <img className="onb-pledge-seal" data-visible={signatureVisible || undefined} src="/images/onboarding/ap-compass-wax-seal.png" alt="" aria-hidden="true" />
              </article>
              {signatureState === 'idle' || signatureState === 'error' ? (
                <button className="onb-primary" type="button" onClick={signAndFinish}>Sign</button>
              ) : (
                <p className="onb-status" role="status">{signatureState === 'signed' ? <><CheckCircle2 aria-hidden="true" /> Signed</> : 'Signing…'}</p>
              )}
            </>
          ) : null}

          {error ? <p className="onb-error" role="alert">{error}</p> : null}
        </motion.section>
      </AnimatePresence>

      {showFooter ? (
        <footer className="onb-footer">
          <button className="onb-primary" type="button" disabled={saving} onClick={next}>
            {isManaging && step === 'subjects' ? (saving ? 'Saving…' : 'Save courses') : 'Continue'} <ArrowRight />
          </button>
        </footer>
      ) : null}
    </main>
  );
}
